import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useCallback, useEffect, useMemo } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { FormSections, Input, Select, Spinner, TextArea, useLocalizeMessage, } from 'libs.nucleus';
import { useCharts } from '../../hooks';
export const CreateChartForm = ({ name, description, selectedDatasetId, onFormChange, onDatasetChange, onValidationChange, }) => {
    const translate = useLocalizeMessage();
    const { checkChartNameUnique, fetchDatasets } = useCharts();
    const [nameError, setNameError] = useState('');
    const [isValidatingName, setIsValidatingName] = useState(false);
    const [datasets, setDatasets] = useState([]);
    const [isNameValid, setIsNameValid] = useState(false);
    // Load datasets on mount
    useEffect(() => {
        const loadDatasets = async () => {
            const result = await fetchDatasets();
            setDatasets(result);
        };
        loadDatasets();
    }, [fetchDatasets]);
    // Validate form: name must be valid and dataset must be selected
    useEffect(() => {
        onValidationChange(isNameValid && !!selectedDatasetId);
    }, [isNameValid, selectedDatasetId, onValidationChange]);
    // Create a debounced function to validate the name
    const validateName = useDebouncedCallback(async (nameValue) => {
        if (!nameValue.trim()) {
            setNameError(translate('Name is required'));
            setIsValidatingName(false);
            setIsNameValid(false);
            return;
        }
        setIsValidatingName(true);
        try {
            const isUnique = await checkChartNameUnique(nameValue);
            if (!isUnique) {
                setNameError(translate('A chart with this name already exists'));
                setIsNameValid(false);
            }
            else {
                setNameError('');
                setIsNameValid(true);
            }
        }
        catch (error) {
            setNameError('');
            setIsNameValid(true);
        }
        finally {
            setIsValidatingName(false);
        }
    }, 500);
    // Trigger validation when name changes
    useEffect(() => {
        if (name) {
            validateName(name);
        }
        else {
            setNameError('');
            setIsNameValid(false);
        }
    }, [name, validateName]);
    const handleNameChange = useCallback((event) => {
        const newName = event.target.value;
        setNameError('');
        onFormChange(newName, description);
    }, [description, onFormChange]);
    const handleDescriptionChange = useCallback((event) => {
        onFormChange(name, event.target.value);
    }, [name, onFormChange]);
    const handleDatasetChange = useCallback((option) => {
        const dataset = datasets.find((d) => d.id === option.value);
        if (dataset) {
            onDatasetChange(dataset.id, dataset.key);
        }
    }, [datasets, onDatasetChange]);
    const datasetOptions = useMemo(() => datasets.map((d) => ({ label: d.name, value: d.id })), [datasets]);
    const formSections = [
        {
            title: translate('Details'),
            content: (_jsxs("div", { className: 'flex flex-col gap-6 max-w-input-lg', children: [_jsxs("div", { className: 'relative', children: [_jsx(Input, { id: 'chart-name', label: translate('Name'), value: name, onChange: handleNameChange, required: true, width: 'full', containLabels: true, hasError: !!nameError, errorMessage: nameError, autoComplete: 'off' }), isValidatingName && (_jsx("div", { className: 'absolute end-0 top-0', children: _jsx(Spinner, { size: 'sm' }) }))] }), _jsx(TextArea, { id: 'chart-description', label: translate('Description'), value: description, onChange: handleDescriptionChange, width: 'full' })] })),
        },
        {
            title: translate('Data source'),
            content: (_jsx("div", { className: 'flex flex-col gap-6 max-w-input-lg', children: _jsx(Select, { label: translate('Dataset'), required: true, placeholder: translate('Select a dataset'), options: datasetOptions, value: datasetOptions.find((o) => o.value === selectedDatasetId), onChange: handleDatasetChange, width: 'full', searchableOptions: true }) })),
        },
    ];
    return (_jsx("div", { className: 'size-full', children: _jsx(FormSections, { sections: formSections }) }));
};
